import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link, useT } from "@/i18n";
import { has, POI_CATEGORIES } from "@/lib/api";

const chaletIcon = L.divIcon({ className: "", html: '<div class="chalet-marker"></div>', iconSize: [22, 22], iconAnchor: [11, 11] });
const poiIcon = (fav) => L.divIcon({ className: "", html: `<div class="poi-marker ${fav ? "fav" : ""}"></div>`, iconSize: [14, 14], iconAnchor: [7, 7] });

export default function PoiMap({ pois, settings, onOpen, height = "h-[420px] sm:h-[560px]" }) {
  const t = useT();
  const [cat, setCat] = useState("Tous");
  const located = useMemo(() => pois.filter((p) => has(p.lat) && has(p.lng)), [pois]);
  const cats = useMemo(() => ["Tous", ...POI_CATEGORIES.filter((c) => located.some((p) => p.category === c))], [located]);
  const shown = cat === "Tous" ? located : located.filter((p) => p.category === cat);
  const center = [settings.map_lat || 44.7736, settings.map_lng || 6.7413];
  const [ready, setReady] = useState(false);
  useEffect(() => { setReady(true); }, []);
  if (!ready) return <div className={`${height} rounded-2xl bg-cream`} />;
  return (
    <div data-testid="poi-map">
      <div className="flex flex-wrap gap-2 mb-4" role="group" aria-label={t("Filtrer la carte par catégorie")}>
        {cats.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`map-filter-${c}`} className={`px-4 py-2 rounded-full text-xs font-medium border transition-colors ${cat === c ? "bg-forest text-ecru border-forest" : "bg-white text-forest border-sand hover:border-forest"}`}>{t(c)}</button>
        ))}
      </div>
      <div className={`${height} rounded-2xl overflow-hidden border border-sand`}>
        <MapContainer center={center} zoom={settings.map_zoom || 12} scrollWheelZoom={false} className="w-full h-full">
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <Marker position={center} icon={chaletIcon}>
            <Popup><strong>Le Chalet Badouille Izoard</strong><br />{settings.address_line1}<br />{settings.address_line2}</Popup>
          </Marker>
          {shown.map((p) => (
            <Marker key={p.id} position={[p.lat, p.lng]} icon={poiIcon(p.favorite)}>
              <Popup>
                <div className="text-sm min-w-[180px]">
                  <p className="text-[10px] uppercase tracking-wider text-sage">{t(p.category)}{p.favorite ? ` · ${t("Coup de cœur")}` : ""}</p>
                  <p className="font-semibold text-forest">{p.name}</p>
                  {has(p.short_description) && <p className="text-xs text-charcoal/70 mt-1">{p.short_description}</p>}
                  {(has(p.distance) || has(p.travel_time)) && <p className="text-xs mt-1">{[p.distance, p.travel_time].filter(has).join(" · ")}</p>}
                  <div className="flex gap-3 mt-2">
                    {onOpen && <button onClick={() => onOpen(p)} className="text-xs font-medium underline">{t("Voir la fiche")}</button>}
                    {has(p.link) && <a href={p.link} target="_blank" rel="noreferrer" className="text-xs underline">{t("Site externe")}</a>}
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
      {located.length === 0 && <p className="text-xs text-charcoal/50 mt-2">{t("Les points d'intérêt géolocalisés apparaîtront ici dès qu'ils seront renseignés dans l'administration.")}</p>}
      {!onOpen && <p className="text-xs text-charcoal/50 mt-2"><Link to="/decouvrir-le-queyras/carte" className="underline">{t("Voir la carte complète")}</Link></p>}
    </div>
  );
}
